import { trackEvent, trackProps } from '../../helpers/utils/analytics';
import {
  lazy,
  startTransition,
  Suspense,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  getDealerIdHistory,
  getCurrentDealerId,
} from '../../helpers/math/spadesMath';
import { GlobalContext } from '../../store/global-context';
import { rotateDealerId } from '../../helpers/utils/perspective';
import { Badge, Box } from '../ui';
import { pressDim, pressSink } from './press';
import { initialFirstDealerOrder } from '../../helpers/utils/constants';
import type { Round, Names } from '../../types';

const DealerSelectionModal = lazy(
  () => import('../modals/DealerSelectionModal'),
);

export interface DealerTagProps {
  id: string;
  index?: number;
  isCurrent: boolean;
  roundHistory: Round[];
  /** Names for the dealer picker when they aren't saved yet (Home). */
  names?: Names;
  /** Home's Different-teams setup holds its own first dealer, so the saved
   *  game stays untouched until Start: show this dealer, and hand a choice
   *  to `onSelect` instead of changing the round's dealer. */
  setup?: { dealerId: string; onSelect: (dealerId: string) => void };
  hint?: boolean;
}

const DealerTag = ({
  id,
  index,
  isCurrent,
  roundHistory,
  names: namesOverride,
  setup,
  hint = false,
  ...props
}: DealerTagProps) => {
  const {
    firstDealerOrder,
    currentRound,
    setDealerOverride,
    setRoundHistory,
    role,
    seat,
    names,
  } = useContext(GlobalContext);
  const [isOpen, setIsOpen] = useState(false);

  const dealerIdHistory = getDealerIdHistory(roundHistory, firstDealerOrder);

  const currentDealerId =
    setup?.dealerId ??
    getCurrentDealerId({
      dealerIdHistory,
      index: index as number,
      isCurrent,
      firstDealerOrder,
      dealerOverride: isCurrent ? currentRound?.dealerOverride : null,
      roundHistory,
    });

  // The dealer is a fact about a player, not a screen slot. In viewer mode the
  // board is rotated to the viewer's seat, so rotate the dealer id too — the
  // badge must follow the player, otherwise it lands on the wrong name.
  const displayDealerId =
    role === 'viewer' ? rotateDealerId(currentDealerId, seat) : currentDealerId;

  const isDealer = displayDealerId === id;
  const canChangeDealer =
    role !== 'viewer' &&
    (isCurrent || (index !== undefined && !!roundHistory[index]));

  const dealerOptions = useMemo(() => {
    const nameMap = namesOverride || names || ({} as Partial<Names>);

    const getNameForId = (playerId: string) => {
      const isTeam1 = playerId.includes('team1BidsAndMade');
      const isP1 = playerId.includes('p1Bid');
      if (isTeam1 && isP1) return nameMap.t1p1Name || 'Team 1 - P1';
      if (isTeam1 && !isP1) return nameMap.t1p2Name || 'Team 1 - P2';
      if (!isTeam1 && isP1) return nameMap.t2p1Name || 'Team 2 - P1';
      return nameMap.t2p2Name || 'Team 2 - P2';
    };

    const options = initialFirstDealerOrder.map((optId) => ({
      id: optId,
      label: getNameForId(optId),
    }));
    return options;
  }, [namesOverride, names]);

  const onSelectDealer = (selectedId: string) => {
    if (!canChangeDealer) {
      setIsOpen(false);
      return;
    }
    if (setup) {
      setup.onSelect(selectedId);
    } else if (isCurrent) {
      setDealerOverride(selectedId);
    } else if (index !== undefined) {
      // Freeze the next dealer so a correction applies to this round only.
      // The normal rotation can then continue from that unchanged dealer.
      const next = [...roundHistory];
      next[index] = { ...next[index], dealerOverride: selectedId };
      if (next[index + 1]) {
        next[index + 1] = {
          ...next[index + 1],
          dealerOverride: dealerIdHistory[index + 1],
        };
      } else {
        setDealerOverride(
          getCurrentDealerId({
            dealerIdHistory,
            index: roundHistory.length,
            isCurrent: true,
            firstDealerOrder,
            dealerOverride: currentRound?.dealerOverride,
            roundHistory,
          }),
        );
      }
      setRoundHistory(next);
    }
    if (selectedId !== currentDealerId) trackEvent('dealer_changed');
    setIsOpen(false);
  };

  const handleBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // The picker is lazy: opening it in a transition waits for its code
    // instead of showing the Suspense fallback, which React then holds for
    // 300ms before the picker can appear.
    if (canChangeDealer) {
      startTransition(() => setIsOpen(true));
    }
  };

  return (
    isDealer && (
      <>
        {hint ? (
          <Box
            as="button"
            // @ts-expect-error Box renders a button here
            type="button"
            aria-label="Change dealer"
            {...trackProps('dealer_picker_opened')}
            onClick={handleBadgeClick}
            css={pressDim}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spades-spacing-xs)',
              cursor: 'pointer',
            }}
          >
            <Badge
              as="span"
              data-cy="dealerBadge"
              data-testid="dealerBadge"
              variant="dealer"
              width="20px"
              height="20px"
              fontSize="10px"
              mx="2px"
            >
              D
            </Badge>
            <span>DEALS FIRST · TAP TO CHANGE</span>
          </Box>
        ) : (
        <Badge
          as={canChangeDealer ? 'button' : 'span'}
          {...(canChangeDealer
            ? { type: 'button' as const, 'aria-label': 'Change dealer' }
            : {})}
          data-cy="dealerBadge"
          data-testid="dealerBadge"
          {...(canChangeDealer ? trackProps('dealer_picker_opened') : {})}
          onClick={handleBadgeClick}
          cursor={canChangeDealer ? 'pointer' : 'default'}
          css={canChangeDealer ? pressSink : undefined}
          variant="dealer"
          mx="2px"
          {...props}
        >
          D
        </Badge>
        )}
        {/* Always mounted, so the transition above can wait here. */}
        <Suspense fallback={null}>
          {isOpen && (
            <DealerSelectionModal
              isOpen={isOpen}
              onClose={setIsOpen}
              dealerOptions={dealerOptions}
              onSelectDealer={onSelectDealer}
              selectedDealerId={currentDealerId}
            />
          )}
        </Suspense>
      </>
    )
  );
};

export default DealerTag;
